// client/src/pages/business/Cart.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import StorefrontBanner from '../../components/common/StorefrontBanner';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useCart } from '../../contexts/CartContext';
import { ordersAPI, productDiscountsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../services/api'; 
import { getImageUrl } from '../../utils/imageHelper';

const getStockIndicator = (stockStatus) => {
  switch (stockStatus) {
    case 'out_of_stock': return { color: 'bg-red-500', label: 'Out of Stock' };
    case 'low_stock': return { color: 'bg-yellow-500', label: 'Low Stock' };
    case 'in_stock':
    default: return { color: 'bg-green-500', label: 'In Stock' };
  }
};

const Cart = () => {
  const { t } = useTranslation('business');
  const { items, clearCart, removeFromCart, updateQuantity } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 1024px)');
  
  const [showCheckout, setShowCheckout] = useState(false);
  const [loading, setLoading] = useState(false);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [enrichedItems, setEnrichedItems] = useState([]);
  const [loadingStock, setLoadingStock] = useState(true);
  
  // State for discounts
  const [discounts, setDiscounts] = useState({});
  const [catalogDiscounts, setCatalogDiscounts] = useState({}); // { wholesalerId: { discounts: [...raw] } }
  const [productDiscounts, setProductDiscounts] = useState({}); // { wholesalerId: { productId: { percentage, reason } } }
  
  // ERP real-time pricing
  const [erpPricing, setErpPricing] = useState({}); // { [wholesalerId]: { lines, totals } }
  const [erpLoading, setErpLoading] = useState({}); // { [wholesalerId]: boolean }

  const [checkoutData, setCheckoutData] = useState({
    deliveryType: 'delivery',
    address: '',
    scheduledDate: '',
    instructions: '',
    paymentMethod: 'cash'
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [orderError, setOrderError] = useState('');
  const [showDiscountBreakdown, setShowDiscountBreakdown] = useState(false);

  // 1. Fetch Business Profile
  useEffect(() => {
    const fetchBusinessProfile = async () => {
      if (!user) return;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/business/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBusinessProfile(response.data);
      } catch (error) {
        console.error('Error fetching business profile:', error);
      }
    };
    fetchBusinessProfile();
  }, [user]);

  // 2. Enrich Items (Stock Status)
  useEffect(() => {
    const enrichCartItems = async () => {
      if (items.length === 0) {
        setEnrichedItems([]);
        setLoadingStock(false);
        return;
      }
      setLoadingStock(true);
      try {
        const token = localStorage.getItem('token');
        const enrichedData = await Promise.all(
          items.map(async (item) => {
            try {
              const response = await axios.get(`${API_URL}/products/${item._id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              const product = response.data?.data || response.data;
              // Normalize wholesalerId to a plain string ID (the endpoint populates it into an object)
              const rawWid = product.wholesalerId;
              const normalizedWid = (rawWid && typeof rawWid === 'object' && rawWid._id)
                ? String(rawWid._id)
                : rawWid || item.wholesalerId;
              return {
                ...item,
                wholesalerId: normalizedWid,
                wholesalerName: item.wholesalerName || (typeof rawWid === 'object' && rawWid.companyName) || '',
                brand: product.brand || item.brand || '',
                category: product.category || item.category || '',
                stockStatus: product.stockStatus || 'in_stock',
                available: product.stockStatus !== 'out_of_stock'
              };
            } catch (error) {
              return { ...item, stockStatus: 'in_stock', available: true };
            }
          })
        );
        setEnrichedItems(enrichedData);
      } catch (error) {
        setEnrichedItems(items.map(item => ({ ...item, stockStatus: 'in_stock', available: true })));
      } finally {
        setLoadingStock(false);
      }
    };
    enrichCartItems();
  }, [items]);

  // ✅ 3. FIX: Fetch Discounts (Correctly Extracting ID)
  useEffect(() => {
    const fetchDiscounts = async () => {
      if (!businessProfile?.business?._id || enrichedItems.length === 0) return;

      const uniqueWholesalerIds = [...new Set(enrichedItems.map(item => {
        const rawId = item.wholesalerId || (item.wholesaler && item.wholesaler._id) || item.wholesaler;
        return (rawId && typeof rawId === 'object' && rawId._id) ? rawId._id : rawId;
      }))].filter(Boolean);

      const newDiscounts = {};

      await Promise.all(uniqueWholesalerIds.map(async (wid) => {
        try {
          const wholesalerIdStr = String(wid);
          const token = localStorage.getItem('token');
          const res = await axios.get(
            `${API_URL}/discounts/customers/business/${businessProfile.business._id}/details?wholesalerId=${wholesalerIdStr}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (res.data.success && res.data.hasDiscounts) {
            // Split into flat and targeted for per-item calculation
            const allDiscs = res.data.discounts || [];
            const flat = allDiscs.filter(d => !d.targetType || d.targetType === 'all');
            const targeted = allDiscs.filter(d => d.targetType && d.targetType !== 'all');
            const flatPct = Math.min(flat.reduce((s, d) => s + d.percentage, 0), 100);
            const minAmount = res.data.combined?.lowestMinimum || 0;
            newDiscounts[wholesalerIdStr] = {
              percentage: flatPct,
              minimumOrderAmount: minAmount,
              targeted
            };
          }
        } catch (err) {
          console.error(`Failed to fetch discount for wholesaler ${wid}`, err);
        }
      }));

      setDiscounts(newDiscounts);
    };

    // Fetch catalog-wide discounts per wholesaler (store raw for per-item targeted matching)
    const fetchCatalogDiscounts = async () => {
      if (enrichedItems.length === 0) return;
      const uniqueWholesalerIds = [...new Set(enrichedItems.map(item => {
        const rawId = item.wholesalerId || (item.wholesaler && item.wholesaler._id) || item.wholesaler;
        return (rawId && typeof rawId === 'object' && rawId._id) ? rawId._id : rawId;
      }))].filter(Boolean);

      const newCatalogDiscounts = {};
      await Promise.all(uniqueWholesalerIds.map(async (wid) => {
        try {
          const res = await axios.get(`${API_URL}/catalog-discounts/wholesaler/${String(wid)}`);
          if (res.data.discounts && res.data.discounts.length > 0) {
            newCatalogDiscounts[String(wid)] = { discounts: res.data.discounts };
          }
        } catch (err) { /* silent */ }
      }));
      setCatalogDiscounts(newCatalogDiscounts);
    };

    // Fetch per-customer per-product discounts
    const fetchProductDiscounts = async () => {
      if (enrichedItems.length === 0) return;
      const uniqueWholesalerIds = [...new Set(enrichedItems.map(item => {
        const rawId = item.wholesalerId || (item.wholesaler && item.wholesaler._id) || item.wholesaler;
        return (rawId && typeof rawId === 'object' && rawId._id) ? rawId._id : rawId;
      }))].filter(Boolean);

      const newProductDiscounts = {};
      await Promise.all(uniqueWholesalerIds.map(async (wid) => {
        try {
          const widStr = String(wid);
          const res = await productDiscountsAPI.getMyDiscounts(widStr);
          if (res.data.success && res.data.discounts) {
            const discMap = res.data.discounts;
            // discounts could be a map {} or array [] — handle both
            if (typeof discMap === 'object' && !Array.isArray(discMap) && Object.keys(discMap).length > 0) {
              newProductDiscounts[widStr] = discMap;
            }
          }
        } catch (err) {
          console.error('[ProductDiscounts] fetch error for wid:', String(wid), err.response?.status, err.message);
        }
      }));
      setProductDiscounts(newProductDiscounts);
    };

    fetchDiscounts();
    fetchCatalogDiscounts();
    fetchProductDiscounts();
  }, [enrichedItems, businessProfile]);

  // 3b. ERP real-time pricing (debounced)
  useEffect(() => {
    if (enrichedItems.length === 0) {
      setErpPricing({});
      return;
    }

    // Group items by wholesaler
    const byWholesaler = {};
    enrichedItems.forEach(item => {
      if (item.available === false) return;
      let wid = item.wholesalerId || (item.wholesaler && item.wholesaler._id) || item.wholesaler;
      if (wid && typeof wid === 'object' && wid._id) wid = wid._id;
      if (!wid) return;
      const widStr = String(wid);
      if (!byWholesaler[widStr]) byWholesaler[widStr] = [];
      byWholesaler[widStr].push({ productId: item._id || item.productId, quantity: item.quantity });
    });

    const timer = setTimeout(async () => {
      const wids = Object.keys(byWholesaler);
      // Set loading for all wholesalers
      const loadingState = {};
      wids.forEach(wid => { loadingState[wid] = true; });
      setErpLoading(prev => ({ ...prev, ...loadingState }));

      const newPricing = {};
      await Promise.all(wids.map(async (wid) => {
        try {
          const res = await ordersAPI.calculateErp({
            wholesalerId: wid,
            items: byWholesaler[wid]
          });
          if (res.data?.erpAvailable && res.data.pricing) {
            newPricing[wid] = res.data.pricing;
          }
        } catch (err) {
          // Silent fallback to platform pricing
        }
      }));

      setErpPricing(prev => {
        const updated = { ...prev };
        // Update or remove per-wholesaler
        wids.forEach(wid => {
          if (newPricing[wid]) updated[wid] = newPricing[wid];
          else delete updated[wid];
        });
        return updated;
      });
      const doneLoading = {};
      wids.forEach(wid => { doneLoading[wid] = false; });
      setErpLoading(prev => ({ ...prev, ...doneLoading }));
    }, 800);

    return () => clearTimeout(timer);
  }, [enrichedItems]);

  // 4. Pre-fill Address
  useEffect(() => {
    if (!businessProfile?.business?.address) return;
    const addr = businessProfile.business.address;
    let fullAddress = '';
    if (typeof addr === 'string') {
      fullAddress = addr;
    } else if (typeof addr === 'object') {
      const { street, city, state, zipCode, postalCode } = addr;
      fullAddress = [street, city, state, zipCode || postalCode].filter(Boolean).join(', ');
    }
    if (fullAddress && fullAddress !== checkoutData.address) {
      setCheckoutData(prev => ({ ...prev, address: fullAddress }));
    }
  }, [businessProfile]);

  const displayItems = loadingStock ? items : enrichedItems;
  
  // . Calculate Subtotals & Discounts
   const cartSubtotal = displayItems.reduce((total, item) => {
     if (item.available === false) return total;
     // Use flash offer price if available, otherwise regular price
     const effectivePrice = item.flashOfferPrice || item.pricing.unitPrice;
     return total + (effectivePrice * item.quantity);
   }, 0);
  
  // ✅ TRACK BOTH AMOUNT AND PERCENTAGE
  let totalDiscountAmount = 0;
  let displayPercentage = 0; // New variable to store the percentage for display
  
  // Helper: calculate catalog discount for a single item, respecting stacking + tiered
  // Returns { pct, breakdown: [{ name, pct, type }] } for display
  const getItemCatalogDiscount = (item, discountData, orderTotal) => {
    if (!discountData || !discountData.length) return { pct: 0, breakdown: [] };
    
    const applicable = discountData.map(d => {
      if (d.type === 'flat') {
        return { pct: d.percentage || 0, stackable: d.stackable, name: d.name, type: 'flat' };
      }
      if (d.type === 'targeted') {
        if (d.targetType === 'brand') {
          if ((item.brand || '').toLowerCase() === (d.targetValue || '').toLowerCase()) {
            return { pct: d.targetPercentage || 0, stackable: d.stackable, name: d.name, type: 'targeted' };
          }
          return null;
        }
        if (d.targetType === 'category') {
          if ((item.category || '').toLowerCase() === (d.targetValue || '').toLowerCase()) {
            return { pct: d.targetPercentage || 0, stackable: d.stackable, name: d.name, type: 'targeted' };
          }
          return null;
        }
        if (d.targetType === 'subcategory') {
          if ((item.subcategory || '').toLowerCase() === (d.targetValue || '').toLowerCase()) {
            return { pct: d.targetPercentage || 0, stackable: d.stackable, name: d.name, type: 'targeted' };
          }
          return null;
        }
        return null;
      }
      if (d.type === 'tiered' && d.tiers) {
        const matchingTier = [...d.tiers]
          .sort((a, b) => b.minAmount - a.minAmount)
          .find(tier => orderTotal >= tier.minAmount && (tier.maxAmount == null || orderTotal < tier.maxAmount));
        if (matchingTier && matchingTier.percentage > 0) {
          return { pct: matchingTier.percentage, stackable: d.stackable, name: d.name, type: 'tiered' };
        }
        return null;
      }
      return null;
    }).filter(Boolean);

    if (applicable.length === 0) return { pct: 0, breakdown: [] };

    const stackable = applicable.filter(a => a.stackable);
    const nonStackable = applicable.filter(a => !a.stackable);
    const stackedPct = stackable.reduce((sum, a) => sum + (a.pct || 0), 0);
    const bestNS = nonStackable.length > 0
      ? nonStackable.reduce((best, a) => (a.pct || 0) > best.pct ? a : best, { pct: 0 })
      : { pct: 0 };

    let finalPct;
    let breakdown;
    if (stackedPct >= bestNS.pct) {
      finalPct = stackedPct;
      breakdown = stackable.map(a => ({ name: a.name, pct: a.pct, type: a.type }));
    } else {
      finalPct = bestNS.pct;
      breakdown = [{ name: bestNS.name, pct: bestNS.pct, type: bestNS.type }];
    }

    return {
      pct: Math.round(Math.min(finalPct, 100) * 100) / 100,
      breakdown
    };
  };

  // Group by wholesaler to apply minimum order checks
  const wholesalerSubtotals = {};
  displayItems.forEach(item => {
    if (item.available === false) return;
    
    let wid = item.wholesalerId || (item.wholesaler && item.wholesaler._id) || item.wholesaler;
    if (wid && typeof wid === 'object' && wid._id) wid = wid._id;
    
    if (wid) {
       const widStr = String(wid);
       const effectivePrice = item.flashOfferPrice || item.pricing.unitPrice;
       const itemTotal = effectivePrice * item.quantity;
       wholesalerSubtotals[widStr] = (wholesalerSubtotals[widStr] || 0) + itemTotal;
    }
  });

  // Per-item catalog discount calculation with breakdown
  let totalCatalogDiscountAmount = 0;
  let catalogDisplayPct = 0;
  const itemDiscountMap = {}; // { productId: { pct, amount, breakdown } }
  
  displayItems.forEach(item => {
    if (item.available === false) return;
    // Flash offer = final price — no catalog discounts stacked on top
    if (item.flashOfferPrice) return;
    let wid = item.wholesalerId || (item.wholesaler && item.wholesaler._id) || item.wholesaler;
    if (wid && typeof wid === 'object' && wid._id) wid = wid._id;
    if (!wid) return;

    const catDisc = catalogDiscounts[String(wid)];
    if (!catDisc?.discounts?.length) return;

    const effectivePrice = item.pricing.unitPrice;
    const itemTotal = effectivePrice * item.quantity;
    const orderTotal = wholesalerSubtotals[wid] || 0;

    const result = getItemCatalogDiscount(item, catDisc.discounts, orderTotal);

    if (result.pct > 0) {
      const discountAmount = Math.round(itemTotal * result.pct) / 100;
      totalCatalogDiscountAmount += discountAmount;
      itemDiscountMap[item.productId || item._id] = {
        pct: result.pct,
        amount: discountAmount,
        breakdown: result.breakdown
      };
    }
  });

  // Calculate a display percentage (weighted average across all items)
  if (cartSubtotal > 0 && totalCatalogDiscountAmount > 0) {
    catalogDisplayPct = Math.round(totalCatalogDiscountAmount / cartSubtotal * 10000) / 100;
  }

  // ── PRODUCT-SPECIFIC DISCOUNTS + CUSTOMER DISCOUNT ──
  // Per item: use product discount only if it's >= the flat customer discount %.
  // Otherwise the item falls back to the customer discount. Whichever wins, replaces the other.
  // Flash offer rule: skip product discount if flash offer gives a better final price.
  let totalProductDiscountAmount = 0;
  const itemProductDiscountMap = {}; // { productId: { pct, amount, reason } }

  Object.keys(wholesalerSubtotals).forEach(wid => {
    const sub = wholesalerSubtotals[wid];

    // Get flat customer discount % for this wholesaler
    let wCatalogAmt = 0;
    displayItems.forEach(item => {
      if (item.available === false) return;
      let itemWid = item.wholesalerId || (item.wholesaler && item.wholesaler._id) || item.wholesaler;
      if (itemWid && typeof itemWid === 'object' && itemWid._id) itemWid = itemWid._id;
      if (String(itemWid) !== String(wid)) return;
      const key = item.productId || item._id;
      if (itemDiscountMap[key]) wCatalogAmt += itemDiscountMap[key].amount;
    });

    const afterCatalog = sub - wCatalogAmt;
    const disc = discounts[wid];
    const flatPct = (disc && afterCatalog >= (disc.minimumOrderAmount || 0)) ? disc.percentage : 0;
    if (flatPct > 0) displayPercentage = flatPct;
    const targetedCustomerDiscs = (disc && afterCatalog >= (disc.minimumOrderAmount || 0)) ? (disc.targeted || []) : [];

    const wProductDiscounts = productDiscounts[wid] || {};

    displayItems.forEach(item => {
      if (item.available === false) return;
      // Flash offer = final price — no customer/product discounts on top
      if (item.flashOfferPrice) return;
      let itemWid = item.wholesalerId || (item.wholesaler && item.wholesaler._id) || item.wholesaler;
      if (itemWid && typeof itemWid === 'object' && itemWid._id) itemWid = itemWid._id;
      if (String(itemWid) !== String(wid)) return;

      const pid = item.productId || item._id;
      const effectivePrice = item.pricing.unitPrice;
      const itemTotal = effectivePrice * item.quantity;
      const catDisc = itemDiscountMap[pid];
      const afterCatItemTotal = catDisc ? itemTotal - catDisc.amount : itemTotal;

      // Calculate per-item customer discount (flat + matching targeted)
      let itemCustomerPct = flatPct;
      for (const td of targetedCustomerDiscs) {
        if (td.targetType === 'category' && (item.category || '').toLowerCase() === (td.targetValue || '').toLowerCase()) {
          itemCustomerPct += td.percentage;
        } else if (td.targetType === 'subcategory' && (item.subcategory || '').toLowerCase() === (td.targetValue || '').toLowerCase()) {
          itemCustomerPct += td.percentage;
        }
      }
      if (itemCustomerPct > 100) itemCustomerPct = 100;

      const pd = wProductDiscounts[pid];

      if (pd && pd.percentage >= itemCustomerPct) {
        // Product discount wins — apply it, skip customer discount for this item
        const itemDiscount = Math.round(afterCatItemTotal * pd.percentage) / 100;
        if (itemDiscount > 0) {
          totalProductDiscountAmount += itemDiscount;
          itemProductDiscountMap[pid] = { pct: pd.percentage, amount: itemDiscount, reason: pd.reason };
        }
        return; // skip customer discount for this item
      }

      // No product discount or customer discount is better — apply customer discount (flat + targeted)
      if (itemCustomerPct > 0) {
        totalDiscountAmount += Math.round(afterCatItemTotal * itemCustomerPct) / 100;
      }
    });
  });

  const cartTotal = cartSubtotal - totalCatalogDiscountAmount - totalDiscountAmount - totalProductDiscountAmount;
  const hasUnavailableItems = enrichedItems.some(item => !item.available);

  // ERP-aware totals: if any wholesaler has ERP pricing, blend it with platform totals
  const hasAnyErpPricing = Object.keys(erpPricing).length > 0;
  const erpAwareSubtotal = hasAnyErpPricing
    ? displayItems.reduce((sum, item) => {
        if (item.available === false) return sum;
        let wid = item.wholesalerId || (item.wholesaler && item.wholesaler._id) || item.wholesaler;
        if (wid && typeof wid === 'object' && wid._id) wid = wid._id;
        const erpLine = erpPricing[String(wid)]?.lines?.find(l => l.productId === (item._id || item.productId));
        if (erpLine) return sum + erpLine.unitPrice * erpLine.quantity;
        return sum + (item.flashOfferPrice || item.pricing.unitPrice) * item.quantity;
      }, 0)
    : cartSubtotal;
  const erpAwareTotal = hasAnyErpPricing
    ? displayItems.reduce((sum, item) => {
        if (item.available === false) return sum;
        let wid = item.wholesalerId || (item.wholesaler && item.wholesaler._id) || item.wholesaler;
        if (wid && typeof wid === 'object' && wid._id) wid = wid._id;
        const erpLine = erpPricing[String(wid)]?.lines?.find(l => l.productId === (item._id || item.productId));
        if (erpLine) return sum + erpLine.lineTotal;
        // Flash items: use flash price, no discounts applied (catAmt/pdAmt are 0)
        const effectivePrice = item.flashOfferPrice || item.pricing.unitPrice;
        const pid = item.productId || item._id;
        const catAmt = itemDiscountMap[pid]?.amount || 0;
        const pdAmt = itemProductDiscountMap[pid]?.amount || 0;
        return sum + effectivePrice * item.quantity - catAmt - pdAmt;
      }, 0)
    : cartTotal;
  const erpTotalDiscount = hasAnyErpPricing ? erpAwareSubtotal - erpAwareTotal : 0;

  const handleCheckoutDataChange = (e) => {
    const { name, value } = e.target;
    setCheckoutData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) setFieldErrors(prev => ({ ...prev, [name]: '' }));
    if (orderError) setOrderError('');
  };

  const handleCheckout = async () => {
    const errors = {};
    if (!items.length) { errors.cart = t('cart.emptyCart'); }
    if (checkoutData.deliveryType === 'delivery' && !checkoutData.address) { errors.address = t('cart.addressRequired'); }
    if (!checkoutData.scheduledDate) { errors.scheduledDate = t('cart.dateRequired'); }
    if (!checkoutData.paymentMethod) { errors.paymentMethod = t('cart.paymentRequired'); }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setTimeout(() => document.querySelector('.field-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      return;
    }
    setFieldErrors({});

    setLoading(true);

    const ordersByWholesaler = displayItems.reduce((acc, item) => {
      let rawId = item.wholesalerId || (item.wholesaler && item.wholesaler._id) || item.wholesaler;
      if (rawId && typeof rawId === 'object' && rawId._id) rawId = rawId._id;
      
      if (!rawId) return acc;
      if (!acc[rawId]) acc[rawId] = [];
      acc[rawId].push(item);
      return acc;
    }, {});

    if (Object.keys(ordersByWholesaler).length === 0) {
      setOrderError(t('wholesalers.cartMissingWholesaler'));
      setLoading(false);
      return;
    }

    try {
      const orderPromises = Object.entries(ordersByWholesaler).map(
        async ([wholesalerId, orderItems]) => {
            const subtotal = orderItems.reduce((sum, item) => {
             const price = item.flashOfferPrice || item.pricing.unitPrice;
             return sum + price * item.quantity;
            }, 0);
          
            const flashOfferIds = [...new Set(
             orderItems
               .filter(item => item.flashOfferId)
               .map(item => item.flashOfferId)
            )];

          const orderData = {
            wholesalerId,
            items: orderItems.map((item) => ({
              productId: item._id,
              name: item.name,
              quantity: item.quantity,
              unitPrice: item.flashOfferPrice || item.pricing.unitPrice,
              originalPrice: item.flashOfferPrice ? item.pricing.unitPrice : undefined,
              unit: item.pricing.unit,
              totalPrice: (item.flashOfferPrice || item.pricing.unitPrice) * item.quantity,
              flashOfferId: item.flashOfferId || undefined,
            })),
            ...(flashOfferIds.length > 0 && { flashOfferIds }),
            delivery: {
              type: checkoutData.deliveryType,
              address: checkoutData.address,
              scheduledFor: checkoutData.scheduledDate,
              instructions: checkoutData.instructions,
            },
            totals: {
              subtotal,
              deliveryFee: 0,
              total: subtotal,
            },
            paymentMethod: checkoutData.paymentMethod,
          };

          return ordersAPI.create(orderData);
        }
      );

      await Promise.all(orderPromises);
      clearCart();
      navigate('/my-orders');
    } catch (err) {
      setOrderError(err.response?.data?.message || t('cart.orderError'));
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <StorefrontBanner />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl lg:text-3xl font-bold text-gray-800 mb-6">{t('cart.title')}</h1>
          
          {items.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-24 h-24 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              <p className="text-gray-500 text-xl mb-4">{t('cart.empty')}</p>
              <button onClick={() => navigate('/wholesalers')} className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 font-semibold">{t('cart.browseWholesalers')}</button>
            </div>
          ) : (
            <div className="space-y-6">
              {hasUnavailableItems && (
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0"><span className="text-2xl">⚠️</span></div>
                    <div>
                      <h4 className="font-semibold text-red-900 mb-1">Items Out of Stock</h4>
                      <p className="text-sm text-red-800">Some items in your cart are currently out of stock and will not be included in your order.</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-6 lg:space-y-4">
                {displayItems.map(item => {
                  const stockInfo = getStockIndicator(item.stockStatus || 'in_stock');
                  const isUnavailable = item.available === false;
                  // ERP pricing lookup for this item
                  let itemWid = item.wholesalerId || (item.wholesaler && item.wholesaler._id) || item.wholesaler;
                  if (itemWid && typeof itemWid === 'object' && itemWid._id) itemWid = itemWid._id;
                  const erpData = erpPricing[String(itemWid)];
                  const erpLine = erpData?.lines?.find(l => l.productId === (item._id || item.productId));
                  const erpItemLoading = erpLoading[String(itemWid)];
                  return (
                  <div key={item._id} className={`bg-white rounded-lg shadow-md p-6 lg:p-4 ${isUnavailable ? 'opacity-60' : ''}`}>
                    <div className="flex flex-col lg:flex-row gap-6 lg:gap-4">
                      <div className="flex-shrink-0">
                        <img 
                          src={getImageUrl(item.images && item.images.length > 0 ? item.images[0] : null)}
                          alt={item.name} 
                          className="w-24 h-24 lg:w-32 lg:h-32 object-cover rounded-lg"
                          onError={(e) => {
                            if (!e.target.dataset.errorHandled) {
                              e.target.dataset.errorHandled = 'true';
                              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect width='200' height='200' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='18' fill='%239ca3af'%3ENo Image%3C/text%3E%3C/svg%3E";
                            }
                          }}
                        />
                      </div>
                      <div className="flex-1 space-y-4 lg:space-y-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg lg:text-lg font-semibold text-gray-900">{item.name}</h3>
                            <div className="flex items-center gap-1.5"><div className={`w-2.5 h-2.5 rounded-full ${stockInfo.color}`}></div><span className="text-xs text-gray-500">{stockInfo.label}</span></div>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{item.wholesalerName || item.wholesaler?.companyName || item.wholesalerId?.companyName || 'Unknown Wholesaler'}</p>
                          {isUnavailable && <div className="mt-2 bg-red-50 border border-red-200 rounded-md p-2"><p className="text-sm text-red-800 font-medium">⚠️ This item is out of stock</p></div>}
                          {item.category && <p className="text-sm text-gray-500 mt-1">{t('catalog.category')}: {item.category}</p>}
                        </div>
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                          <div>
                            {item.hidePricing ? (
                              <p className="text-base text-gray-400 italic">{t('catalog.priceOnAgreement', 'Price on agreement')}</p>
                            ) : erpLine ? (
                              <div className="flex items-center gap-2">
                                {erpLine.discountPct > 0 && (
                                  <span className="text-sm text-gray-400 line-through">€{erpLine.unitPrice.toFixed(2)}</span>
                                )}
                                <span className={`text-base lg:text-lg font-bold ${erpLine.discountPct > 0 ? 'text-emerald-600' : 'text-blue-600'}`}>
                                  €{(erpLine.discountPct > 0 ? (erpLine.lineTotal / erpLine.quantity) : erpLine.unitPrice).toFixed(2)} / {item.pricing.unit}
                                </span>
                                {erpItemLoading && <span className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></span>}
                              </div>
                            ) : item.flashOfferPrice ? (
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-400 line-through">€{item.pricing.unitPrice.toFixed(2)}</span>
                                <span className="text-base lg:text-lg font-bold text-red-600">€{item.flashOfferPrice.toFixed(2)} / {item.pricing.unit}</span>
                                <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-semibold">⚡ Flash</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <p className="text-base lg:text-lg font-bold text-blue-600">€{item.pricing.unitPrice.toFixed(2)} / {item.pricing.unit}</p>
                                {erpItemLoading && <span className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></span>}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-3 lg:gap-2">
                            <label className="text-sm font-medium text-gray-700 lg:hidden">{t('cart.quantity')}:</label>
                            <div className="flex items-center gap-2 lg:gap-1">
                              {(() => { const step = item.pricing?.minOrderQuantity || 1; return (<>
                              <button onClick={() => updateQuantity(item._id, Math.max(step, item.quantity - step))} disabled={isUnavailable} className="w-10 h-10 lg:w-8 lg:h-8 rounded-lg bg-gray-200">-</button>
                              <input type="number" min={step} step={step} value={item.quantity} placeholder={step > 1 ? `Min ${step}` : undefined} title={step > 1 ? t('cart.minQuantity', 'Minimum quantity: {{min}}', { min: step }) : undefined} onChange={(e) => updateQuantity(item._id, Math.max(step, parseInt(e.target.value) || step))} className="w-16 lg:w-14 h-10 border-2 rounded-lg text-center" />
                              <button onClick={() => updateQuantity(item._id, item.quantity + step)} disabled={isUnavailable} className="w-10 h-10 lg:w-8 lg:h-8 rounded-lg bg-gray-200">+</button>
                              {step > 1 && <span className="text-xs text-amber-600 font-medium whitespace-nowrap">Min: {step}</span>}
                              </>); })()}
                            </div>
                          </div>
                          <div className="text-left lg:text-right">
                            <p className="text-sm text-gray-600">{t('cart.total')}</p>
                            {item.hidePricing ? (
                              <p className="text-lg text-gray-400 italic">—</p>
                            ) : erpLine ? (
                              <p className="text-xl lg:text-2xl font-bold text-gray-900">€{erpLine.lineTotal.toFixed(2)}</p>
                            ) : (
                              <p className="text-xl lg:text-2xl font-bold text-gray-900">€{((item.flashOfferPrice || item.pricing.unitPrice) * item.quantity).toFixed(2)}</p>
                            )}
                          </div>
                        </div>
                        {/* Per-item discount badges — ERP pricing replaces platform discounts */}
                        {!item.hidePricing && erpLine ? (
                          erpLine.discountPct > 0 && (
                            <div className="bg-purple-50 rounded-lg px-3 py-2 mt-1">
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <span className="text-xs text-purple-700 font-medium">
                                  🏷️ {t('cart.productDiscount', 'Product Discount')}: -{erpLine.discountPct}%
                                </span>
                                <span className="text-xs text-purple-600 ml-auto font-semibold">
                                  −€{(erpLine.unitPrice * erpLine.quantity - erpLine.lineTotal).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          )
                        ) : (
                        <>
                        {/* Per-item catalog discount breakdown — hidden when hidePricing */}
                        {!item.hidePricing && (() => {
                          const itemDisc = itemDiscountMap[item.productId || item._id];
                          if (!itemDisc || itemDisc.pct === 0) return null;
                          const effectivePrice = item.pricing.unitPrice;
                          const discountedPrice = effectivePrice * (1 - itemDisc.pct / 100);
                          return (
                            <div className="bg-blue-50 rounded-lg px-3 py-2 mt-1">
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                {itemDisc.breakdown.map((b, i) => (
                                  <span key={i} className="text-xs text-blue-700 font-medium">
                                    {b.type === 'targeted' ? '🎯' : b.type === 'tiered' ? '📊' : '🏷️'} {b.name}: -{b.pct}%
                                  </span>
                                ))}
                                <span className="text-xs text-blue-600 ml-auto font-semibold">
                                  →&nbsp;€{discountedPrice.toFixed(2)}/{item.pricing.unit} (−€{(Math.round(effectivePrice * item.quantity * itemDisc.pct) / 100).toFixed(2)})
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                        {/* Per-item product-specific discount badge */}
                        {!item.hidePricing && (() => {
                          const pdDisc = itemProductDiscountMap[item.productId || item._id];
                          if (!pdDisc) return null;
                          return (
                            <div className="bg-purple-50 rounded-lg px-3 py-2 mt-1">
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <span className="text-xs text-purple-700 font-medium">
                                  🏷️ {t('cart.productDiscount', 'Product Discount')}: -{pdDisc.pct}%
                                </span>
                                {pdDisc.reason && (
                                  <span className="text-xs text-purple-500">({pdDisc.reason})</span>
                                )}
                                <span className="text-xs text-purple-600 ml-auto font-semibold">
                                  −€{pdDisc.amount.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          );
                        })()}
                        </>
                        )}
                        <div className="flex justify-end"><button onClick={() => removeFromCart(item._id)} className="text-red-600 font-medium px-6 py-2">{t('cart.removeFromCart')}</button></div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">{t('cart.orderSummary')}</h2>
                {items.some(i => i.hidePricing) ? (
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-base">
                      <span>{t('cart.itemCount', 'Items')}</span>
                      <span className="font-semibold">{items.reduce((s, i) => s + i.quantity, 0)}</span>
                    </div>
                    <div className="border-t pt-2 mt-2">
                      <p className="text-sm text-gray-500 italic text-center py-4">{t('storefront.pricingConfirmedByWholesaler', 'Pricing confirmed by wholesaler')}</p>
                    </div>
                  </div>
                ) : (
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-base">
                    <span>{t('cart.subtotalNet')}</span>
                    <span className="font-semibold">€{(hasAnyErpPricing ? erpAwareSubtotal : cartSubtotal).toFixed(2)}</span>
                  </div>
                  {hasAnyErpPricing && erpTotalDiscount > 0 ? (
                    <>
                      <div
                        className="flex justify-between items-center text-base text-emerald-700 font-medium cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1"
                        onClick={() => setShowDiscountBreakdown(!showDiscountBreakdown)}
                      >
                        <span className="flex items-center gap-1.5">
                          <svg className={`w-3.5 h-3.5 transition-transform ${showDiscountBreakdown ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                          {t('cart.discountsApplied', 'Discounts applied')}
                        </span>
                        <span>- €{erpTotalDiscount.toFixed(2)}</span>
                      </div>
                      {showDiscountBreakdown && (
                        <div className="ml-5 space-y-1 text-sm">
                          {Object.values(erpPricing).flatMap(wp => wp.lines || []).filter(l => l.discountPct > 0).map((line, i) => {
                            const item = enrichedItems.find(ei => (ei._id || ei.productId) === line.productId);
                            const saving = line.unitPrice * line.quantity - line.lineTotal;
                            return (
                              <div key={i} className="flex justify-between text-purple-600">
                                <span className="truncate mr-2">{item?.name || t('cart.product', 'Product')} (-{line.discountPct}%)</span>
                                <span className="flex-shrink-0">- €{saving.toFixed(2)}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  ) : !hasAnyErpPricing && (totalCatalogDiscountAmount > 0 || totalDiscountAmount > 0 || totalProductDiscountAmount > 0) ? (
                    <>
                      <div
                        className="flex justify-between items-center text-base text-emerald-700 font-medium cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1"
                        onClick={() => setShowDiscountBreakdown(!showDiscountBreakdown)}
                      >
                        <span className="flex items-center gap-1.5">
                          <svg className={`w-3.5 h-3.5 transition-transform ${showDiscountBreakdown ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                          {t('cart.discountsApplied', 'Discounts applied')}
                        </span>
                        <span>- €{(totalCatalogDiscountAmount + totalDiscountAmount + totalProductDiscountAmount).toFixed(2)}</span>
                      </div>
                      {showDiscountBreakdown && (
                        <div className="ml-5 space-y-1 text-sm">
                          {totalCatalogDiscountAmount > 0 && (
                            <div className="flex justify-between text-blue-600">
                              <span>{t('cart.catalogDiscount', 'Catalog Discount')} ({catalogDisplayPct.toFixed(1)}%)</span>
                              <span>- €{totalCatalogDiscountAmount.toFixed(2)}</span>
                            </div>
                          )}
                          {totalDiscountAmount > 0 && (
                            <div className="flex justify-between text-green-600">
                              <span>{t('cart.customerDiscount', 'Customer Discount')} ({displayPercentage}%)</span>
                              <span>- €{totalDiscountAmount.toFixed(2)}</span>
                            </div>
                          )}
                          {totalProductDiscountAmount > 0 && (
                            <div className="flex justify-between text-purple-600">
                              <span>{t('cart.productDiscount', 'Product Discount')} ({Object.keys(itemProductDiscountMap).length} {t('cart.items', 'items')})</span>
                              <span>- €{totalProductDiscountAmount.toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : null}
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between text-xl font-bold">
                      <span>{t('cart.grandTotal')}</span>
                      <span className="text-blue-600">€{(hasAnyErpPricing ? erpAwareTotal : cartTotal).toFixed(2)}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 italic">{t('cart.vatDisclaimer')}</p>
                </div>
                )}
                <button onClick={() => setShowCheckout(!showCheckout)} className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold">{showCheckout ? t('cart.cancel') : t('cart.checkout')}</button>
              </div>
            </div>
          )}

          {showCheckout && items.length > 0 && (
            <div className="mt-6 bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">{t('cart.orderDetails')}</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-base font-medium mb-2">{t('cart.deliveryType')} *</label>
                  <div className="space-y-2">
                    <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer"><input type="radio" name="deliveryType" value="delivery" checked={checkoutData.deliveryType === 'delivery'} onChange={handleCheckoutDataChange} /><span className="ml-3 font-medium">{t('cart.delivery')}</span></label>
                    <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer"><input type="radio" name="deliveryType" value="pickup" checked={checkoutData.deliveryType === 'pickup'} onChange={handleCheckoutDataChange} /><span className="ml-3 font-medium">{t('cart.pickup')}</span></label>
                  </div>
                </div>

                {orderError && (
                  <div className="field-error bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{orderError}</div>
                )}
                {fieldErrors.cart && (
                  <div className="field-error bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{fieldErrors.cart}</div>
                )}

                {checkoutData.deliveryType === 'delivery' && (
                  <div>
                    <label className="block font-medium mb-2">{t('cart.address')} *</label>
                    <textarea name="address" value={checkoutData.address} onChange={handleCheckoutDataChange} className={`w-full p-3 border-2 rounded-lg ${fieldErrors.address ? 'border-red-300' : ''}`} required />
                    {fieldErrors.address && <p className="field-error text-red-500 text-xs mt-1">{fieldErrors.address}</p>}
                  </div>
                )}

                <div>
                  <label className="block font-medium mb-2">{t('cart.scheduledDate')} *</label>
                  <input type="date" name="scheduledDate" value={checkoutData.scheduledDate} onChange={handleCheckoutDataChange} onClick={(e) => e.target.showPicker?.()} onFocus={(e) => e.target.showPicker?.()} min={today} className={`w-full p-3 border-2 rounded-lg cursor-pointer ${fieldErrors.scheduledDate ? 'border-red-300' : ''}`} required />
                  {fieldErrors.scheduledDate && <p className="field-error text-red-500 text-xs mt-1">{fieldErrors.scheduledDate}</p>}
                </div>
                <div><label className="block font-medium mb-2">{t('cart.instructions')}</label><textarea name="instructions" value={checkoutData.instructions} onChange={handleCheckoutDataChange} className="w-full p-3 border-2 rounded-lg" rows="3" /></div>
                <div>
                  <label className="block font-medium mb-2">{t('cart.paymentMethod')} *</label>
                  <select name="paymentMethod" value={checkoutData.paymentMethod} onChange={handleCheckoutDataChange} className={`w-full p-3 border-2 rounded-lg ${fieldErrors.paymentMethod ? 'border-red-300' : ''}`}>
                    <option value="cash">{t('cart.cash')}</option><option value="bank_transfer">{t('cart.transfer')}</option>
                  </select>
                  {fieldErrors.paymentMethod && <p className="field-error text-red-500 text-xs mt-1">{fieldErrors.paymentMethod}</p>}
                </div>

                <div className="pt-4">
                  <button onClick={handleCheckout} disabled={loading} className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold disabled:opacity-50">
                    {loading ? t('cart.processing') : items.some(i => i.hidePricing) ? t('cart.placeOrder') : `${t('cart.placeOrder')} (€${(hasAnyErpPricing ? erpAwareTotal : cartTotal).toFixed(2)})`}
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default Cart;